import $ from 'jquery';
import voca from 'voca';
import { getLogger } from '@cld/upload-widget';
import appConfig from 'appConfig';
import { throwOnDev } from 'utils/envUtils';
import { getPreparedMetadataValues } from 'common/metadataDeps';
import consts from '../common/consts';
import { types as ats } from '../store/actions';
import { selectActiveQueueFilesIds, selectRunningQueueFilesIds, selectQueues, selectFileQueueIndex, selectCloudInfo } from '../store/selectors';
import { getUploadFieldFromContainer, getAcceptFileTypes, stringifyMetadata } from '../common/utils';

const logger = getLogger();

const getQueueIndexForFile = (id, state) => {
  const queueIndx = selectFileQueueIndex(state)[id];

  if (!~queueIndx) {
    throwOnDev(`[uploader.uploadFileMethods]: didnt find queue for file with id: ${id}`);
  }

  return queueIndx;
};

const getFileObjectForId = (id, options) => {
  const state = options.getState();
  const queueIndx = getQueueIndexForFile(id, state);
  const queues = selectQueues(state);

  let result;

  if (~queueIndx && queues[queueIndx]) {
    const fileOps = queues[queueIndx].files[id];

    if (fileOps) {
      result = fileOps.getFileObject();
    }
  } else {
    logger.warn(`[uploader.uploadFileMethods]: couldn't find queue for file id: ${id}`);
  }

  if (!result) {
    throwOnDev(`[uploader.uploadFileMethods]: didnt find file object for id: ${id}`);
  }

  return result;
};

const getFileDataForId = (id, options) => {
  const state = options.getState();
  const queueIndx = getQueueIndexForFile(id, state);
  const queue = selectQueues(state)[queueIndx];

  let result;

  if (queue && queue.filesData) {
    result = queue.filesData[id];
  }

  if (!result) {
    throw new Error(`[uploader.uploadFileMethods]: didnt find file data for file with id: ${id}`);
  }

  return result;
};

const getQueueConfigForId = (id, options) => {
  const state = options.getState();
  const queueIndx = getQueueIndexForFile(id, state);

  const queue = ~queueIndx ? selectQueues(state)[queueIndx] : null;

  return queue && (queue.configOverride || queue.config);
};

const isFileInCurrentQueue = (id, options) => {
  const state = options.getState();
  const runningQueueIds = selectRunningQueueFilesIds(state);

  let isActive = false;

  if (runningQueueIds) {
    isActive = runningQueueIds && ~runningQueueIds.indexOf(id);
  } else {
    const queueIds = selectActiveQueueFilesIds(state);
    isActive = queueIds && ~queueIds.indexOf(id);
  }

  return !!isActive;
};

const isFileInQueues = (id, options) => {
  const index = selectFileQueueIndex(options.getState())[id];
  return index || index === 0;
};

const getThumbnailUrlOptions = (result, queueConfig, options, isSmallSize = true) => {
  const cloudInfo = selectCloudInfo(options.getState()).info || {};

  const urlOptions = {
    cloud_name: queueConfig.cloudName,
    private_cdn: cloudInfo['data-private-cdn'],
    cname: cloudInfo['data-cname'],
    secure_distribution: queueConfig.secureDistribution ? queueConfig.secureDistribution : cloudInfo['data-secure-distribution'],
    format: result.format === 'png' ? 'png' : 'jpg',
    version: result.version,
    resource_type: result.resource_type,
    type: result.type,
    secure: true,
  };

  const thumbTrans = queueConfig.thumbnailTransformation;

  if (thumbTrans) {
    if (voca.isString(thumbTrans)) {
      urlOptions.raw_transformation = thumbTrans;
    } else {
      urlOptions.transformation = thumbTrans;
    }
  } else if (isSmallSize) {
    urlOptions.width = 90;
    urlOptions.height = 60;
    urlOptions.crop = 'limit';
  }

  return urlOptions;
};

const isShowThumbnail = (type) => [consts.RESOURCE_TYPES.IMAGE, consts.RESOURCE_TYPES.VIDEO].includes(type);

const getThumbnailUrl = (queueConfig, result, options, isSmallSize = true) => {
  let thumbUrl;

  if (queueConfig) {
    const urlOptions = getThumbnailUrlOptions(result, queueConfig, options, isSmallSize);
    thumbUrl = $.cloudinary.url(result.public_id, urlOptions);
  }

  return thumbUrl;
};

const getIsPreviewUrlRequiredForFormat = (format) => appConfig.ui.imagePreviewFormats.includes(format);

const finalizeFileUpload = (id, success, result, options) => {
  if (success && typeof result === 'object') {
    const queueConfig = isShowThumbnail(result.resource_type) && getQueueConfigForId(id, options);
    const thumbUrl = queueConfig?.thumbnails !== false && getThumbnailUrl(queueConfig, result, options);

    if (thumbUrl) {
      result = {
        ...result,
        thumbnail_url: thumbUrl,
      };
    }

    if (queueConfig && getIsPreviewUrlRequiredForFormat(result.format)) {
      result.preview_url = getThumbnailUrl(queueConfig, result, options, false);
    }
  }

  options.actions[ats.SET_UPLOADER_FILE_DONE]({ id, success, result });

  const data = getFileDataForId(id, options); // get latest data so we can send to external

  logger.log(`[uploader.uploadFileMethods]: retrieved file data for finalized upload [id: ${id}]`, data);
};

const updateFileProgress = (id, progress, options) => options.actions[ats.SET_UPLOADER_FILE_PROGRESS]({ id, progress });

const updateFileDataMethods = (id, data, options) => {
  options.actions[ats.SET_UPLOADER_FILE_DATA_METHODS]({
    id,
    methods: {
      submitUpload: data.submit.bind(data),
      abortUpload: data.abort && data.abort.bind(data),
    },
  });
};

const unsetFilePreCallsDelayed = (ids, options) => {
  options.actions[ats.SET_UPLOADER_FILE_PRECALLS_DELAYED_OFF]({ ids });
};

const updateFilePreview = (id, data, options) => {
  options.actions[ats.SET_UPLOADER_FILE_PREVIEW]({
    id,
    preview: data.preview || data.files[0].preview,
    img: data.img,
    exif: data.exif,
  });
};

const getFilePreparedParams = (id, options) => {
  const fileData = getFileDataForId(id, options);
  return fileData.preparedParams;
};

const getCustomCoordinates = (id, options) => {
  const fileData = getFileDataForId(id, options);
  return fileData.customCoordinates;
};

const setAllowedFileTypesForFile = (id, file, container, options) => {
  const setFileTypes = (types) => {
    const field = getUploadFieldFromContainer(container);
    field.fileupload('option', 'acceptFileTypes', types);
  };

  if (voca.isString(file)) {
    logger.log('[uploader.uploadFileMethods]: turning off file type option for url based upload');
    setFileTypes(null);
  } else {
    const fileData = getFileDataForId(id, options);

    if (fileData.camera) {
      logger.log('[uploader.uploadFileMethods]: turning off accepted-file-types config for Camera based upload');
      setFileTypes(null);
    } else {
      logger.log('[uploader.uploadFileMethods]: turning on file accepted-file-types config for File based upload');
      const config = getQueueConfigForId(id, options);

      if (config) {
        setFileTypes(getAcceptFileTypes(config.clientAllowedFormats));
      }
    }
  }
};

const getQueueFilesDataFromFileId = (id, state) => {
  const qIndex = getQueueIndexForFile(id, state);
  const queues = selectQueues(state);

  const queue = queues[qIndex];

  return queue.filesData;
};

const getQueueMetadataFromFileId = (id, state) => {
  const qIndex = getQueueIndexForFile(id, state);
  const queues = selectQueues(state);

  const queue = queues[qIndex];

  return queue.metadata;
};

/**
 * returns structured metadata for the file being uploaded
 * only when metadata values were provided in Metadata UI
 * will filter out fields that marked as keep for existing for files that are in conflict (info from ML)
 */
const getMetadataForFile = (id, options) => {
  let result;

  const state = options.getState(),
    queueMetadata = getQueueMetadataFromFileId(id, state);

  if (queueMetadata) {
    const { values, conflicts, fields } = queueMetadata,
      isInConflict = conflicts?.find((file) => file.id === id),
      filtered = getPreparedMetadataValues(values, fields, !!isInConflict);

    result = stringifyMetadata(filtered);

    logger.log(`[uploader.uploadFileMethods]: file '${id}' has structured metadata: '${result}'`);
  }

  return result;
};

const //for small files use short intervals: 0.5 second
  FETCH_SHORT_INTERVAL = 500,
  //for bigger files use longer intervals: 3 seconds
  FETCH_LONG_INTERVAL = 3000,
  //the max percentage to reach
  FETCH_MAX_PROGRESS = 92;

const initFetchUploadProgress = (id, options) => {
  let { size } = getFileDataForId(id, options);
  size = parseInt(size);

  const predictedFileUploadTime = Math.floor(size / appConfig.upload.fetchFakeTimeFactor) * 1000,
    //use long interval when predicted time is more than twice as long
    useMaxInterval = predictedFileUploadTime > FETCH_LONG_INTERVAL * 2;

  //calculate how many times progress event will be fired
  const eventNum = useMaxInterval ? predictedFileUploadTime / FETCH_LONG_INTERVAL : predictedFileUploadTime / FETCH_SHORT_INTERVAL;

  if (eventNum > 0) {
    let counter = 0;

    const handler = setInterval(
      () => {
        let contProgress = false;

        if (counter < eventNum) {
          let data;
          try {
            data = getFileDataForId(id, options);
          } catch (e) {
            throwOnDev(`[uploader.uploadFileMethods]: didnt find file data for id: ${id}`);
          }
          const finished = !data || data.done || data.failed || data.aborted;

          if (!finished && data) {
            const progress =
              eventNum - counter === 1
                ? //dont reach 100% progress
                  FETCH_MAX_PROGRESS
                : ((counter + 1) * 100) / eventNum;

            if (progress <= FETCH_MAX_PROGRESS) {
              logger.log(`[uploader.uploadFileMethods] fake upload progress update for file: ${id}, progress: ${progress}`);
              updateFileProgress(id, progress, options);
              counter += 1;
              contProgress = true;
            }
          }
        }

        if (!contProgress) {
          clearInterval(handler);
        }
      },
      useMaxInterval ? FETCH_LONG_INTERVAL : FETCH_SHORT_INTERVAL
    );
  }
};

export {
  getFileObjectForId,
  getFileDataForId,
  isFileInCurrentQueue,
  finalizeFileUpload,
  updateFileProgress,
  updateFileDataMethods,
  updateFilePreview,
  getFilePreparedParams,
  setAllowedFileTypesForFile,
  isFileInQueues,
  getCustomCoordinates,
  getQueueConfigForId,
  unsetFilePreCallsDelayed,
  getQueueFilesDataFromFileId,
  getQueueMetadataFromFileId,
  getMetadataForFile,
  initFetchUploadProgress,
};
